import { useCallback, useEffect, useState } from 'react'
import {
  Bot, Check, CheckSquare, Copy, ExternalLink, GitCommit, GitPullRequest, Hand, History, Link2, Receipt,
  ListTree, MessageSquare, MoreHorizontal, Pencil, Plus, Send, Trash2, X,
} from 'lucide-react'
import type {
  Activiteit, ChecklistPunt, Commit, Project, Reactie, Sprint, Taak, TaakPrioriteit, TaakSoort, TaakStatus,
} from '../../../gedeeld/types'
import { useAi, standTekst } from '../../lib/ai'
import { api, github, repoUit } from '../../lib/api'
import { Markdown, zetVink } from '../../lib/markdown'
import {
  datum, datumTijd, eersteRegel, geleden, naarInvoer, PRIORITEITEN, sleutelVan, SOORTEN, STATUSSEN, vanInvoer,
} from '../../lib/opmaak'
import { navigeer } from '../../lib/router'
import { meld, meldFout, openTaak, taakGewijzigd, useBasis, useStaat } from '../../lib/staat'
import {
  Avatar, Foutvlak, Kies, KiesMens, Knop, Lade, Laden, MenuRegel, Tabs, Uitklap, vraagBevestiging,
} from '../ui'
import { Deadline, PrioriteitIcoon, SoortIcoon, StatusPil } from './stukjes'

/* ===========================================================================
 *  Eén taak, als lade over het scherm
 *
 *  Alles wat Jira en Trello bij een kaart hebben, en één ding meer: de taak
 *  IS de rij in public.taak die ook het systeem vult (facturen,
 *  sollicitaties). Wat hier op iemands naam komt, staat bij die persoon in
 *  Mijn werk -- met een seintje erbij (0156). Daarom staat er bij het kiezen
 *  van iemand of hij hier wel mag.
 *
 *  Elk veld slaat meteen op. Geen knop "opslaan" onderaan: bij een lade die
 *  je met Esc sluit, is zo'n knop de plek waar werk verdwijnt. Alleen de
 *  omschrijving heeft een eigen opslaan, omdat je daar langer aan typt en
 *  een half woord geen wijziging is die iemand anders een seintje moet geven.
 * =========================================================================== */

type Onder = 'reacties' | 'activiteit' | 'code'

export default function TaakLade({ id }: { id: string }) {
  const { mensen, projecten } = useBasis()
  const github_ = useStaat((s) => s.github)
  const ik = useStaat((s) => s.ik)
  const [taak, setTaak] = useState<Taak | null>(null)
  const [reacties, setReacties] = useState<Reactie[]>([])
  const [subtaken, setSubtaken] = useState<Taak[]>([])
  const [sprints, setSprints] = useState<Sprint[]>([])
  const [fout, setFout] = useState<string | null>(null)
  const [onder, setOnder] = useState<Onder>('reacties')

  const sluit = useCallback(() => openTaak(null), [])

  const laad = useCallback(async () => {
    setFout(null)
    try {
      const x = await api.taak(id)
      setTaak(x.taak)
      setReacties(x.reacties)
      setSubtaken(x.subtaken)
      if (x.taak.projectId) {
        api.project(x.taak.projectId).then((p) => setSprints(p.sprints)).catch(() => setSprints([]))
      }
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    }
  }, [id])

  useEffect(() => { void laad() }, [laad])

  const project = projecten.find((p) => p.id === taak?.projectId) ?? null

  /** Eén of meer velden opslaan, en het scherm alvast bijwerken. */
  const bewaar = useCallback(async (deel: Partial<Taak>) => {
    if (!taak) return
    const voor = taak
    setTaak({ ...taak, ...deel })
    try {
      const na = await api.taakOpslaan({ id: taak.id, ...deel })
      setTaak(na)
      taakGewijzigd(na)
    } catch (e) {
      setTaak(voor)
      meldFout(e)
    }
  }, [taak])

  if (fout) {
    return (
      <Lade onSluit={sluit}>
        <div className="lade-kop"><span className="vul" /><Knop soort="stil" klein icoon={<X size={16} />} onClick={sluit} /></div>
        <div className="lade-lijf"><Foutvlak fout={fout} opnieuw={laad} /></div>
      </Lade>
    )
  }

  if (!taak) return <Lade onSluit={sluit}><Laden tekst="De taak wordt opgehaald…" /></Lade>

  const sleutel = sleutelVan(taak, projecten)
  const toegewezen = mensen.find((m) => m.id === taak.toegewezenAan)

  return (
    <Lade onSluit={sluit} breed={980}>
      <div className="lade-kop">
        <Kies<TaakSoort> klein waarde={taak.soort} titel="Soort"
          opties={SOORTEN.map((s) => ({ key: s.key, label: s.label, icoon: <SoortIcoon soort={s.key} /> }))}
          render={(o) => <><SoortIcoon soort={(o?.key ?? 'taak') as TaakSoort} /><span>{o?.label}</span></>}
          onKies={(s) => void bewaar({ soort: s })} />
        {project && (
          <button type="button" className="kruimel" onClick={() => { sluit(); navigeer(`/p/${project.id}/bord`) }}>
            <span className={`punt t-${project.kleur}`} />{project.naam}
          </button>
        )}
        {sleutel && <span className="mono stil">{sleutel}</span>}
        <span className="vul" />
        <Knop soort="stil" klein icoon={<Link2 size={15} />} title="Link kopiëren"
          onClick={() => { void navigator.clipboard.writeText(`${location.origin}/taak/${taak.id}`).then(() => meld('Link gekopieerd')) }} />
        <MeerMenu taak={taak} project={project} githubAan={github_.ingesteld} opBewaar={bewaar} />
        <Knop soort="stil" klein icoon={<X size={16} />} onClick={sluit} aria-label="Sluiten" />
      </div>

      <div className="lade-lijf taak-lijf">
        <div className="taak-hoofd">
          <Titel taak={taak} opBewaar={(titel) => void bewaar({ titel })} />

          <Omschrijving taak={taak} opBewaar={(omschrijving) => bewaar({ omschrijving })}
            opChecklist={(checklist) => bewaar({ checklist })} />

          <Checklist punten={taak.checklist} opBewaar={(checklist) => void bewaar({ checklist })} />

          {taak.soort !== 'subtaak' && (
            <Subtaken ouder={taak} subtaken={subtaken} projecten={projecten}
              opNieuw={(t) => { setSubtaken((x) => [...x, t]); taakGewijzigd(t) }}
              opWijzig={(t) => { setSubtaken((x) => x.map((y) => (y.id === t.id ? t : y))); taakGewijzigd(t) }} />
          )}

          <div className="taak-onder">
            <Tabs<Onder> waarde={onder} onKies={setOnder} tabs={[
              { key: 'reacties', label: 'Reacties', icoon: <MessageSquare size={14} />, teller: reacties.length },
              { key: 'activiteit', label: 'Geschiedenis', icoon: <History size={14} /> },
              ...(github_.ingesteld && sleutel && !sleutel.startsWith('#')
                ? [{ key: 'code' as Onder, label: 'Code', icoon: <GitCommit size={14} /> }] : []),
            ]} />
            {onder === 'reacties' && (
              <Reacties taakId={taak.id} reacties={reacties}
                opNieuw={(r) => setReacties((x) => [...x, r])} />
            )}
            {onder === 'activiteit' && <Geschiedenis taakId={taak.id} taak={taak} />}
            {onder === 'code' && sleutel && <CodeBijTaak sleutel={sleutel} />}
          </div>
        </div>

        <aside className="taak-zij">
          <Eigenschap label="Status">
            <Kies<TaakStatus> waarde={taak.status}
              opties={STATUSSEN.map((s) => ({ key: s.key, label: s.label, kleur: s.kleur }))}
              render={(o) => <StatusPil status={(o?.key ?? 'te_doen') as TaakStatus} />}
              onKies={(status) => void bewaar({ status })} />
          </Eigenschap>

          <Eigenschap label="Toegewezen aan">
            <KiesMens waarde={taak.toegewezenAan} mensen={mensen}
              onKies={(m) => void bewaar({ toegewezenAan: m?.id ?? null, toegewezenNaam: m?.naam ?? null })} />
            {toegewezen && !toegewezen.zietWerk && (
              <small className="waarschuwing">
                {toegewezen.naam} mag niet in Projects. Er komt wel een seintje, maar de taak
                is daarna alleen via dat seintje terug te vinden.
              </small>
            )}
            {!taak.toegewezenAan && taak.toegewezenRol && (
              <small className="stil">
                Ligt bij de rol {taak.toegewezenRol}, nog door niemand opgepakt.
                {ik && ik.rollen.includes(taak.toegewezenRol) && (
                  <> <button type="button" className="knop k-gewoon klein"
                    onClick={() => void bewaar({ toegewezenAan: ik.id, toegewezenNaam: ik.naam, status: 'bezig' })}>
                    <Hand size={13} /> Oppakken
                  </button></>
                )}
              </small>
            )}
          </Eigenschap>

          <Eigenschap label="Prioriteit">
            <Kies<TaakPrioriteit> waarde={taak.prioriteit}
              opties={PRIORITEITEN.map((p) => ({ key: p.key, label: p.label, icoon: <PrioriteitIcoon p={p.key} /> }))}
              onKies={(prioriteit) => void bewaar({ prioriteit })} />
          </Eigenschap>

          {project && (
            <Eigenschap label="Sprint">
              <Kies<string> waarde={taak.sprintId ?? ''}
                opties={[
                  { key: '', label: 'Backlog' },
                  ...sprints.filter((s) => s.status !== 'afgerond' || s.id === taak.sprintId)
                    .map((s) => ({ key: s.id, label: s.naam + (s.status === 'actief' ? ' (loopt)' : ''), kleur: s.status === 'actief' ? 'ok' : 'info' })),
                ]}
                onKies={(s) => void bewaar({ sprintId: s || null })} />
            </Eigenschap>
          )}

          <Eigenschap label="Story points">
            <PuntenKiezer waarde={taak.punten} onKies={(punten) => void bewaar({ punten })} />
          </Eigenschap>

          <Eigenschap label="Labels">
            <LabelKiezer taak={taak} project={project} opBewaar={(labels) => void bewaar({ labels })} />
          </Eigenschap>

          <div className="eigenschap-paar">
            <Eigenschap label="Start">
              <input type="date" className="invoer klein" value={naarInvoer(taak.startAt)}
                onChange={(e) => void bewaar({ startAt: vanInvoer(e.target.value) })} />
            </Eigenschap>
            <Eigenschap label="Deadline">
              <input type="date" className="invoer klein" value={naarInvoer(taak.deadline)}
                onChange={(e) => void bewaar({ deadline: vanInvoer(e.target.value) })} />
              <Deadline taak={taak} />
            </Eigenschap>
          </div>

          {project && taak.soort !== 'epic' && (
            <Eigenschap label="Hoort bij">
              <OuderKiezer taak={taak} project={project} projecten={projecten}
                opKies={(ouderId) => void bewaar({ ouderId })} />
            </Eigenschap>
          )}

          <Eigenschap label="Project">
            <Kies<string> waarde={taak.projectId ?? ''}
              opties={[{ key: '', label: 'Geen project' }, ...projecten.filter((p) => !p.archief || p.id === taak.projectId)
                .map((p) => ({ key: p.id, label: p.naam, kleur: p.kleur }))]}
              onKies={(projectId) => void bewaar({ projectId: projectId || null, sprintId: null, ouderId: null })} />
            <small className="stil">Bij een ander project krijgt de taak een nieuw nummer.</small>
          </Eigenschap>

          {taak.githubIssue && (
            <Eigenschap label="GitHub">
              <a href={taak.githubIssue} target="_blank" rel="noopener noreferrer" className="extern">
                <GitPullRequest size={14} /> {taak.githubIssue.replace('https://github.com/', '')}
                <ExternalLink size={12} />
              </a>
            </Eigenschap>
          )}

          <div className="taak-herkomst">
            <span>Aangemaakt {datumTijd(taak.createdAt)}{taak.doorNaam ? ` door ${taak.doorNaam}` : ''}</span>
            <span>Bijgewerkt {geleden(taak.updatedAt)}</span>
            {taak.klaarAt && <span>Klaar op {datum(taak.klaarAt)}{taak.klaarDoorNaam ? ` door ${taak.klaarDoorNaam}` : ''}</span>}
            {taak.bron === 'factuur' && taak.bronId ? (
              /* De factuur zelf staat in TW10 Online; daar teken je hem. Afvinken
                 hier tekent niets -- de taak gaat vanzelf dicht als de stap gezet is. */
              <a className="extern" href={`https://tw10online.truckwash-workspace.com/inkoop/${encodeURIComponent(taak.bronId)}`}
                target="_blank" rel="noopener noreferrer">
                <Receipt size={13} /> Open de factuur in TW10 Online <ExternalLink size={12} />
              </a>
            ) : taak.bron !== 'handmatig' && <span>Komt van {taak.bron}</span>}
          </div>
        </aside>
      </div>
    </Lade>
  )
}

function Eigenschap({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="eigenschap">
      <span className="eigenschap-label">{label}</span>
      <div className="eigenschap-waarde">{children}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Titel en omschrijving
 * ------------------------------------------------------------------ */

function Titel({ taak, opBewaar }: { taak: Taak; opBewaar: (t: string) => void }) {
  const [tekst, setTekst] = useState(taak.titel)
  useEffect(() => setTekst(taak.titel), [taak.titel])
  const klaar = () => {
    const t = tekst.trim()
    if (t && t !== taak.titel) opBewaar(t)
    else setTekst(taak.titel)
  }
  return (
    <textarea
      className="taak-titel"
      value={tekst}
      rows={1}
      onChange={(e) => setTekst(e.target.value.replace(/\n/g, ' '))}
      onBlur={klaar}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLTextAreaElement).blur() } }}
      aria-label="Titel"
    />
  )
}

function Omschrijving({
  taak, opBewaar, opChecklist,
}: {
  taak: Taak
  opBewaar: (t: string) => Promise<void>
  opChecklist: (c: ChecklistPunt[]) => Promise<void>
}) {
  const [bewerken, setBewerken] = useState(false)
  const [tekst, setTekst] = useState(taak.omschrijving)
  const [bezig, setBezig] = useState(false)
  const ai = useAi()

  useEffect(() => { if (!bewerken) setTekst(taak.omschrijving) }, [taak.omschrijving, bewerken])

  async function opslaan() {
    setBezig(true)
    await opBewaar(tekst)
    setBezig(false)
    setBewerken(false)
  }

  const voorstel = ai.resultaat
  return (
    <section className="taak-blok">
      <div className="blok-kop">
        <h3>Omschrijving</h3>
        <span className="vul" />
        <Knop soort="stil" klein icoon={<Bot size={14} />} bezig={ai.bezig}
          title="Laat de eigen AI een omschrijving en stappen voorstellen"
          onClick={() => void ai.vraag('uitschrijven', { projectId: taak.projectId, titel: taak.titel, omschrijving: tekst })}>
          Uitschrijven
        </Knop>
        {!bewerken && <Knop soort="stil" klein icoon={<Pencil size={14} />} onClick={() => setBewerken(true)}>Bewerken</Knop>}
      </div>

      {ai.bezig && <p className="ai-wacht">{standTekst(ai.stand)}</p>}
      {ai.fout && <Foutvlak fout={ai.fout} />}
      {voorstel && !ai.bezig && (voorstel.omschrijving || voorstel.onleesbaar) && (
        /* Een voorstel staat NAAST de omschrijving en vervangt hem niet: wat
           de AI schrijft is een voorstel, en een mens beslist. */
        <div className="ai-voorstel">
          <div className="blok-kop">
            <Bot size={14} /><strong>Voorstel van de eigen AI</strong>
            <span className="vul" />
            <Knop soort="stil" klein onClick={ai.wis}>Weg</Knop>
            {!voorstel.onleesbaar && (
              <Knop klein soort="hoofd" icoon={<Check size={14} />} onClick={() => {
                setTekst(voorstel.omschrijving ?? '')
                setBewerken(true)
                if (voorstel.checklist?.length) {
                  void opChecklist([
                    ...taak.checklist,
                    ...voorstel.checklist.map((t) => ({ id: crypto.randomUUID(), tekst: t, af: false })),
                  ])
                }
                ai.wis()
              }}>Overnemen en nalezen</Knop>
            )}
          </div>
          {voorstel.onleesbaar
            ? <p className="stil">De AI gaf geen bruikbaar antwoord. Dit kwam terug:</p>
            : null}
          <Markdown tekst={voorstel.omschrijving ?? voorstel.tekst ?? ''} klein />
          {!!voorstel.checklist?.length && (
            <ul className="ai-stappen">{voorstel.checklist.map((c, i) => <li key={i}>{c}</li>)}</ul>
          )}
        </div>
      )}

      {bewerken ? (
        <div className="omschrijving-bewerk">
          <textarea className="invoer" rows={10} value={tekst} autoFocus onChange={(e) => setTekst(e.target.value)}
            placeholder="Wat moet er gebeuren, waarom, en wanneer is het klaar? Markdown mag: **vet**, - lijstjes, - [ ] vinkjes, `code`."
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void opslaan() }} />
          <div className="rij">
            <Knop soort="hoofd" klein bezig={bezig} onClick={() => void opslaan()}>Opslaan</Knop>
            <Knop soort="stil" klein onClick={() => { setTekst(taak.omschrijving); setBewerken(false) }}>Annuleren</Knop>
            <small className="stil">Ctrl+Enter slaat op</small>
          </div>
        </div>
      ) : taak.omschrijving ? (
        <div className="omschrijving" onDoubleClick={() => setBewerken(true)}>
          <Markdown tekst={taak.omschrijving} opties={{
            opVink: (regel, af) => void opBewaar(zetVink(taak.omschrijving, regel, af)),
          }} />
        </div>
      ) : (
        <button type="button" className="leeg-veld" onClick={() => setBewerken(true)}>
          Nog geen omschrijving. Klik om er een te schrijven.
        </button>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ *
 *  Checklist en subtaken
 * ------------------------------------------------------------------ */

function Checklist({ punten, opBewaar }: { punten: ChecklistPunt[]; opBewaar: (c: ChecklistPunt[]) => void }) {
  const [nieuw, setNieuw] = useState('')
  const af = punten.filter((p) => p.af).length

  function voegToe() {
    const t = nieuw.trim()
    if (!t) return
    opBewaar([...punten, { id: crypto.randomUUID(), tekst: t, af: false }])
    setNieuw('')
  }

  return (
    <section className="taak-blok">
      <div className="blok-kop">
        <h3><CheckSquare size={15} /> Checklist</h3>
        {!!punten.length && <span className="stil">{af} van {punten.length}</span>}
      </div>
      {!!punten.length && (
        <div className="voortgang t-ok"><div style={{ width: `${Math.round((af / punten.length) * 100)}%` }} /></div>
      )}
      <ul className="checklist">
        {punten.map((p, i) => (
          <li key={p.id}>
            <input type="checkbox" checked={p.af}
              onChange={() => opBewaar(punten.map((x) => (x.id === p.id ? { ...x, af: !x.af } : x)))} />
            <span className={p.af ? 'af' : ''}>{p.tekst}</span>
            <span className="vul" />
            {i > 0 && (
              <button type="button" className="mini" title="Omhoog"
                onClick={() => { const n = [...punten]; [n[i - 1], n[i]] = [n[i]!, n[i - 1]!]; opBewaar(n) }}>↑</button>
            )}
            <button type="button" className="mini" title="Weghalen" onClick={() => opBewaar(punten.filter((x) => x.id !== p.id))}>
              <X size={13} />
            </button>
          </li>
        ))}
      </ul>
      <div className="rij">
        <input className="invoer klein" value={nieuw} onChange={(e) => setNieuw(e.target.value)}
          placeholder="Een stap toevoegen…" onKeyDown={(e) => { if (e.key === 'Enter') voegToe() }} />
        <Knop klein icoon={<Plus size={14} />} onClick={voegToe} disabled={!nieuw.trim()} />
      </div>
    </section>
  )
}

function Subtaken({
  ouder, subtaken, projecten, opNieuw, opWijzig,
}: {
  ouder: Taak
  subtaken: Taak[]
  projecten: Project[]
  opNieuw: (t: Taak) => void
  opWijzig: (t: Taak) => void
}) {
  const [titel, setTitel] = useState('')
  const [bezig, setBezig] = useState(false)

  async function voegToe() {
    const t = titel.trim()
    if (!t || bezig) return
    setBezig(true)
    try {
      const nieuw = await api.taakOpslaan({
        titel: t, ouderId: ouder.id, projectId: ouder.projectId,
        soort: ouder.soort === 'epic' ? 'taak' : 'subtaak',
        sprintId: ouder.sprintId, prioriteit: ouder.prioriteit,
      })
      opNieuw(nieuw)
      setTitel('')
    } catch (e) { meldFout(e) }
    setBezig(false)
  }

  const klaar = subtaken.filter((s) => s.status === 'klaar').length
  return (
    <section className="taak-blok">
      <div className="blok-kop">
        <h3><ListTree size={15} /> {ouder.soort === 'epic' ? 'Taken in deze epic' : 'Subtaken'}</h3>
        {!!subtaken.length && <span className="stil">{klaar} van {subtaken.length} klaar</span>}
      </div>
      <div className="subtaken">
        {subtaken.map((s) => (
          <div key={s.id} className="subtaak">
            <input type="checkbox" checked={s.status === 'klaar'} title="Klaar"
              onChange={async () => {
                try {
                  opWijzig(await api.taakOpslaan({ id: s.id, status: s.status === 'klaar' ? 'te_doen' : 'klaar' }))
                } catch (e) { meldFout(e) }
              }} />
            <SoortIcoon soort={s.soort} grootte={13} />
            <span className="mono stil">{sleutelVan(s, projecten)}</span>
            <button type="button" className={`link-knop ${s.status === 'klaar' ? 'af' : ''}`} onClick={() => openTaak(s.id)}>{s.titel}</button>
            <span className="vul" />
            <StatusPil status={s.status} />
            <Avatar naam={s.toegewezenNaam} id={s.toegewezenAan} grootte={18} />
          </div>
        ))}
      </div>
      <div className="rij">
        <input className="invoer klein" value={titel} onChange={(e) => setTitel(e.target.value)}
          placeholder={ouder.soort === 'epic' ? 'Een taak aan deze epic toevoegen…' : 'Een subtaak toevoegen…'}
          onKeyDown={(e) => { if (e.key === 'Enter') void voegToe() }} />
        <Knop klein icoon={<Plus size={14} />} bezig={bezig} onClick={() => void voegToe()} disabled={!titel.trim()} />
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ *
 *  Reacties, geschiedenis, code
 * ------------------------------------------------------------------ */

function Reacties({ taakId, reacties, opNieuw }: {
  taakId: string; reacties: Reactie[]; opNieuw: (r: Reactie) => void
}) {
  const [tekst, setTekst] = useState('')
  const [bezig, setBezig] = useState(false)

  async function plaats() {
    const t = tekst.trim()
    if (!t || bezig) return
    setBezig(true)
    try {
      opNieuw(await api.reactie(taakId, t))
      setTekst('')
    } catch (e) { meldFout(e) }
    setBezig(false)
  }

  return (
    <div className="reacties">
      {!reacties.length && <p className="stil">Nog niets. Wie op de taak staat of hem maakte, krijgt een seintje bij een reactie.</p>}
      {reacties.map((r) => (
        <div key={r.id} className="reactie">
          <Avatar naam={r.doorNaam} id={r.door} grootte={26} />
          <div className="reactie-lijf">
            <div className="reactie-kop"><strong>{r.doorNaam ?? 'Iemand'}</strong><span className="stil">{geleden(r.createdAt)}</span></div>
            <Markdown tekst={r.tekst} klein />
          </div>
        </div>
      ))}
      <div className="reactie-nieuw">
        <textarea className="invoer" rows={2} value={tekst} onChange={(e) => setTekst(e.target.value)}
          placeholder="Een reactie… (Ctrl+Enter plaatst)" onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void plaats() }} />
        <Knop soort="hoofd" klein icoon={<Send size={14} />} bezig={bezig} onClick={() => void plaats()} disabled={!tekst.trim()}>Plaatsen</Knop>
      </div>
    </div>
  )
}

function Geschiedenis({ taakId, taak }: { taakId: string; taak: Taak }) {
  const [regels, setRegels] = useState<Activiteit[] | null>(null)
  const [bereikbaar, setBereikbaar] = useState(true)
  useEffect(() => {
    api.activiteit({ taakId, limiet: 100 })
      .then((x) => { setRegels(x.activiteit); setBereikbaar(x.bereikbaar) })
      .catch(() => { setRegels([]); setBereikbaar(false) })
  }, [taakId, taak.updatedAt])

  if (!regels) return <Laden />
  return (
    <div className="geschiedenis">
      {!bereikbaar && <p className="stil">Het logboek staat in MongoDB, en die was niet te bereiken. De taak zelf is in orde.</p>}
      {bereikbaar && !regels.length && <p className="stil">Nog niets gebeurd sinds Projects er is.</p>}
      {regels.map((r) => (
        <div key={r.id} className="geschiedenis-regel">
          <Avatar naam={r.wieNaam} id={r.wie} grootte={18} />
          <span><strong>{r.wieNaam}</strong> {r.tekst}</span>
          <span className="vul" />
          <span className="stil" title={datumTijd(r.at)}>{geleden(r.at)}</span>
        </div>
      ))}
    </div>
  )
}

/** De commits die dit taaknummer noemen, over alle repo's heen. */
function CodeBijTaak({ sleutel }: { sleutel: string }) {
  const [commits, setCommits] = useState<Commit[] | null>(null)
  const [fout, setFout] = useState<string | null>(null)
  useEffect(() => {
    github.zoekCommits(sleutel).then(setCommits).catch((e) => setFout(e instanceof Error ? e.message : String(e)))
  }, [sleutel])
  if (fout) return <Foutvlak fout={fout} />
  if (!commits) return <Laden />
  if (!commits.length) {
    return <p className="stil">Nog geen commits die <span className="mono">{sleutel}</span> noemen. Zet het nummer in je commitbericht, dan staat hij hier.</p>
  }
  return (
    <div className="commit-lijst klein">
      {commits.map((c) => (
        <a key={c.sha} className="commit-regel" href={`/code/${c.repo}/commit/${c.sha}`}
          onClick={(e) => { e.preventDefault(); openTaak(null); navigeer(`/code/${c.repo}/commit/${c.sha}`) }}>
          <Avatar naam={c.auteur.naam} url={c.auteur.avatar} grootte={20} />
          <span className="commit-bericht">{eersteRegel(c.bericht)}</span>
          <span className="stil">{c.repo}</span>
          <span className="mono stil">{c.sha.slice(0, 7)}</span>
          <span className="stil">{geleden(c.datum)}</span>
        </a>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Kiezers in de zijkant
 * ------------------------------------------------------------------ */

const PUNTEN = [0, 1, 2, 3, 5, 8, 13, 21]

function PuntenKiezer({ waarde, onKies }: { waarde: number | null; onKies: (p: number | null) => void }) {
  return (
    <div className="punten-kiezer">
      {PUNTEN.map((p) => (
        <button key={p} type="button" className={waarde === p ? 'actief' : ''} onClick={() => onKies(waarde === p ? null : p)}>{p}</button>
      ))}
    </div>
  )
}

function LabelKiezer({ taak, project, opBewaar }: { taak: Taak; project: Project | null; opBewaar: (l: string[]) => void }) {
  const [open, setOpen] = useState(false)
  const [nieuw, setNieuw] = useState('')
  const bekend = project?.labels ?? []
  const alle = [...new Set([...bekend.map((l) => l.naam), ...taak.labels])]
  const kleur = (n: string) => bekend.find((l) => l.naam === n)?.kleur ?? 'info'

  return (
    <Uitklap open={open} onOpen={setOpen} breed={240}
      knop={
        <button type="button" className="kies label-knop">
          {taak.labels.length
            ? taak.labels.map((l) => <span key={l} className={`label t-${kleur(l)}`}>{l}</span>)
            : <span className="stil">Geen labels</span>}
        </button>
      }>
      <div className="menu-lijst">
        {alle.map((l) => (
          <MenuRegel key={l} actief={taak.labels.includes(l)} icoon={<span className={`punt t-${kleur(l)}`} />}
            onClick={() => opBewaar(taak.labels.includes(l) ? taak.labels.filter((x) => x !== l) : [...taak.labels, l])}>
            {l}
          </MenuRegel>
        ))}
        {!alle.length && <p className="menu-leeg">Dit project heeft nog geen labels. Typ er hieronder een.</p>}
      </div>
      <div className="menu-zoek">
        <Plus size={14} />
        <input value={nieuw} onChange={(e) => setNieuw(e.target.value)} placeholder="Nieuw label…"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && nieuw.trim()) {
              opBewaar([...new Set([...taak.labels, nieuw.trim().slice(0, 32)])])
              setNieuw('')
            }
          }} />
      </div>
    </Uitklap>
  )
}

function OuderKiezer({ taak, project, projecten, opKies }: {
  taak: Taak; project: Project; projecten: Project[]; opKies: (id: string | null) => void
}) {
  const [opties, setOpties] = useState<Taak[] | null>(null)
  useEffect(() => {
    api.taken({ projectId: project.id }).then((t) => setOpties(
      t.filter((x) => x.id !== taak.id && (x.soort === 'epic' || (taak.soort === 'subtaak' && x.soort !== 'subtaak'))),
    )).catch(() => setOpties([]))
  }, [project.id, taak.id, taak.soort])

  return (
    <Kies<string> waarde={taak.ouderId ?? ''}
      opties={[
        { key: '', label: 'Niets' },
        ...(opties ?? []).map((o) => ({ key: o.id, label: `${sleutelVan(o, projecten) ?? ''} ${o.titel}`.trim(), icoon: <SoortIcoon soort={o.soort} /> })),
      ]}
      leeg={opties === null ? 'Laden…' : 'Niets'}
      onKies={(id) => opKies(id || null)} />
  )
}

/* ------------------------------------------------------------------ *
 *  Het menu met de rest
 * ------------------------------------------------------------------ */

function MeerMenu({ taak, project, githubAan, opBewaar }: {
  taak: Taak; project: Project | null; githubAan: boolean; opBewaar: (d: Partial<Taak>) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const repo = repoUit(project?.githubRepo)

  async function naarGithub() {
    setOpen(false)
    if (!repo) return
    try {
      const sleutel = taak.nummer && project?.sleutel ? `${project.sleutel}-${taak.nummer}` : null
      const issue = await github.issueAanmaken(repo, {
        titel: sleutel ? `${sleutel}: ${taak.titel}` : taak.titel,
        tekst: `${taak.omschrijving || '_Geen omschrijving._'}\n\n---\nUit Projects: ${location.origin}/taak/${taak.id}`,
        labels: taak.soort === 'bug' ? ['bug'] : undefined,
      })
      await opBewaar({ githubIssue: issue.url })
      meld(`Issue #${issue.nummer} aangemaakt op GitHub`)
    } catch (e) { meldFout(e) }
  }

  async function weg() {
    setOpen(false)
    if (!(await vraagBevestiging(`"${taak.titel}" weggooien? Dat kan niet ongedaan worden gemaakt. Zet hem liever op klaar als hij af is.`, 'Weggooien'))) return
    try {
      await api.taakVerwijderen(taak.id)
      taakGewijzigd({ id: taak.id, weg: true })
      openTaak(null)
      meld('De taak is weggegooid')
    } catch (e) { meldFout(e) }
  }

  async function kopie() {
    setOpen(false)
    try {
      const n = await api.taakOpslaan({
        titel: taak.titel + ' (kopie)', omschrijving: taak.omschrijving, projectId: taak.projectId,
        soort: taak.soort, prioriteit: taak.prioriteit, labels: taak.labels, punten: taak.punten,
        checklist: taak.checklist.map((c) => ({ ...c, id: crypto.randomUUID(), af: false })),
      })
      taakGewijzigd(n)
      openTaak(n.id)
    } catch (e) { meldFout(e) }
  }

  return (
    <Uitklap open={open} onOpen={setOpen} uitlijnen="rechts" breed={260}
      knop={<Knop soort="stil" klein icoon={<MoreHorizontal size={16} />} aria-label="Meer" />}>
      <MenuRegel icoon={<Copy size={14} />} onClick={() => void kopie()}>Dupliceren</MenuRegel>
      {githubAan && repo && !taak.githubIssue && (
        <MenuRegel icoon={<GitPullRequest size={14} />} onClick={() => void naarGithub()}>Issue maken in {repo}</MenuRegel>
      )}
      {githubAan && !repo && project && (
        <MenuRegel icoon={<GitPullRequest size={14} />} onClick={() => { setOpen(false); navigeer(`/p/${project.id}/instellingen`); openTaak(null) }}>
          Koppel eerst een repo aan {project.naam}
        </MenuRegel>
      )}
      {taak.status !== 'klaar' && (
        <MenuRegel icoon={<Check size={14} />} onClick={() => { setOpen(false); void opBewaar({ status: 'klaar' }) }}>Op klaar zetten</MenuRegel>
      )}
      {taak.bron === 'handmatig' && <MenuRegel gevaar icoon={<Trash2 size={14} />} onClick={() => void weg()}>Weggooien</MenuRegel>}
    </Uitklap>
  )
}
